/**
 * Capabilities — one row per thing the agent can do (Work History, Assets, …),
 * showing which connected source feeds it and its status. Clicking a capability
 * opens its detail (`/capabilities/:capability`) to test or swap the source. The
 * accounts themselves live on the Connections page.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Banner,
  Card,
  Input,
  Skeleton,
  StatusIndicator,
} from '@servicecompressionllc/sc-design-system';
import type { StatusTone } from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { useMe } from '../../hooks/useMe.js';
import type { IntegrationDefinition } from '../../lib/types.js';
import { useIntegrationCatalog } from './useIntegrationCatalog.js';
import { useCapabilitySourcesLogic } from './useCapabilitySourcesLogic.js';
import { CAPABILITY_DESC, capabilityLabel, systemDisplay } from './shared.js';

interface CapabilityRowVM {
  capability: string;
  label: string;
  description: string;
  activeSources: IntegrationDefinition[];
  anyConfigured: boolean;
  status: { tone: StatusTone; label: string };
}

export const CapabilitiesPage = () => {
  const navigate = useNavigate();
  const me = useMe();
  const tenantId = me.data?.tenant.id;
  const catalog = useIntegrationCatalog();
  const caps = useCapabilitySourcesLogic(tenantId);
  const [query, setQuery] = useState('');

  const integrations = useMemo(() => catalog.data?.integrations ?? [], [catalog.data]);

  const rows = useMemo((): CapabilityRowVM[] => {
    const defByKey = new Map(integrations.map((d) => [d.key, d]));
    return (caps.data?.capabilities ?? [])
      .map((c): CapabilityRowVM => {
        const activeSources = c.sources
          .filter((s) => s.active)
          .map((s) => defByKey.get(s.key))
          .filter((d): d is IntegrationDefinition => d !== undefined);
        const anyConfigured = c.sources.some((s) => s.configured);
        const status: { tone: StatusTone; label: string } =
          activeSources.length > 0
            ? {
                tone: 'success',
                label: activeSources.length > 1 ? `${activeSources.length} active` : 'Active',
              }
            : anyConfigured
              ? { tone: 'neutral', label: 'Set up · not active' }
              : { tone: 'neutral', label: 'Not set up' };
        return {
          capability: c.capability,
          label: capabilityLabel(c.capability),
          description: CAPABILITY_DESC[c.capability] ?? '',
          activeSources,
          anyConfigured,
          status,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [caps.data, integrations]);

  const q = query.trim().toLowerCase();
  const visibleRows = q
    ? rows.filter(
        (r) => r.label.toLowerCase().includes(q) || r.description.toLowerCase().includes(q),
      )
    : rows;

  if (me.isLoading || !me.data || catalog.isLoading) {
    return (
      <AppShell>
        <Skeleton className="mb-2 h-7 w-48" />
        <Skeleton className="mb-6 h-4 w-96" />
        <Skeleton className="h-64 w-full" />
      </AppShell>
    );
  }

  if (catalog.error || caps.error) {
    return (
      <AppShell>
        <Banner variant="danger">
          <Banner.Body>
            <Banner.Title>Could not load capabilities.</Banner.Title>
            <Banner.Description>{(catalog.error ?? caps.error)?.message}</Banner.Description>
          </Banner.Body>
        </Banner>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <h2 className="text-ink text-2xl font-bold">Capabilities</h2>
      <p className="text-ink-muted mb-6 mt-1 max-w-[76ch] text-sm">
        Everything the agent can do, and which connected source feeds each one. Open a capability to
        test or swap its source. Connect the accounts themselves under Connections.
      </p>

      <Input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Filter capabilities…"
        aria-label="Filter capabilities"
        className="mb-4 max-w-xs"
      />

      {caps.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : visibleRows.length === 0 ? (
        <p className="text-ink-subtle px-1 py-6 text-sm">No capabilities match “{query}”.</p>
      ) : (
        <Card padding="none">
          <div className="divide-outline divide-y">
            {visibleRows.map((row) => (
              <button
                key={row.capability}
                type="button"
                onClick={() => navigate(`/capabilities/${row.capability}`)}
                className="hover:bg-surface-hover flex w-full items-center gap-4 px-5 py-4 text-left transition-colors"
              >
                <div className="min-w-0 flex-[2]">
                  <div className="text-ink text-[15px] font-semibold">{row.label}</div>
                  {row.description && (
                    <div className="text-ink-subtle mt-0.5 truncate text-[13px]">
                      {row.description}
                    </div>
                  )}
                </div>
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                  {row.activeSources.length > 0 ? (
                    row.activeSources.map((def) => (
                      <Badge key={def.key} variant="neutral">
                        {systemDisplay(def.system)}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-ink-subtle text-[13px]">
                      {row.anyConfigured ? 'configured, none active' : '—'}
                    </span>
                  )}
                </div>
                <StatusIndicator tone={row.status.tone}>{row.status.label}</StatusIndicator>
                <span className="text-ink-subtle text-lg leading-none">›</span>
              </button>
            ))}
          </div>
        </Card>
      )}
    </AppShell>
  );
};
